/**
 * Transformation-service endpoints used by the plugin editor when saving an
 * event's code snippet (Phase 6f). Injected into the shared `integrationApi`
 * (the baseQuery is shared; URLs are fully qualified against
 * `services.transformation`, so no separate store wiring is needed).
 *
 * Wire (all under /transformation):
 *   GET   /v1/transformations/{id}                          → transformation + snippets
 *   POST  /v1/transformation                                → { transformationId, snippetId }
 *   PATCH /v1/transformation/{id}/createSnippet             → { transformationId, snippetId }
 *   PATCH /v1/transformation/{id}/{snippetId}/promote       → (promotes a snippet to live)
 *   POST  /v1/transformation/{id}/transform                 → JSON-encoded STRING of
 *         { pluginVariables, notificationSettings?, hasError, resultDesc?, errorDesc? }
 */
import { services } from '@config/services';
import { integrationApi } from './_base';

export interface TransformationSnippet {
  snippetId?: string;
  code?: string;
  [key: string]: unknown;
}

export interface TransformationDetail {
  transformationId?: string;
  snippets?: TransformationSnippet[];
  [key: string]: unknown;
}

/** Body for createTransformation / createSnippet (outbound snippet). */
export interface TransformationWriteBody {
  code: string;
  creatorId?: string;
  creatorType?: string;
  topic?: string;
  transformationType?: string;
  entityId?: string;
}

export interface TransformationWriteResult {
  transformationId: string;
  snippetId: string;
  [key: string]: unknown;
}

/** Exported request builders (single source of truth; asserted by the
 * wire-shape-lock test — see versions.ts for why builders, not interception). */
export const transformationRequestBuilders = {
  getTransformationDetails: (transformationId: string) => ({
    url: `${services.transformation}/v1/transformations/${encodeURIComponent(transformationId)}`,
    method: 'GET' as const,
  }),
  createTransformation: (body: TransformationWriteBody) => ({
    url: `${services.transformation}/v1/transformation`,
    method: 'POST' as const,
    body,
  }),
  createSnippet: ({ transformationId, data }: { transformationId: string; data: TransformationWriteBody }) => ({
    url: `${services.transformation}/v1/transformation/${encodeURIComponent(transformationId)}/createSnippet`,
    method: 'PATCH' as const,
    body: data,
  }),
  promoteTransformation: ({ transformationId, snippetId }: { transformationId: string; snippetId: string }) => ({
    url: `${services.transformation}/v1/transformation/${encodeURIComponent(transformationId)}/${encodeURIComponent(snippetId)}/promote`,
    method: 'PATCH' as const,
  }),
  testTransformation: ({ snippetCode, payload }: { snippetCode: string; payload: string }) => ({
    url: `${services.transformation}/v1/transformation/test`,
    method: 'POST' as const,
    body: { snippetCode, payload },
  }),
  runTransformation: ({ transformationId, body }: { transformationId: string; body: RunTransformationBody }) => ({
    url: `${services.transformation}/v1/transformation/${encodeURIComponent(transformationId)}/transform`,
    method: 'POST' as const,
    body,
  }),
};

export interface TestTransformationResult {
  transformationResult?: unknown;
  [key: string]: unknown;
}

/** Plugin identity attached to a snippet run so the service can persist a
 * TransformationExecutionLog. The service only writes a log when this is
 * present, and its validator requires every field non-empty — a partial
 * context is rejected as a 400, so callers must build this all-or-nothing. */
export interface TransformPluginContext {
  pluginId: string;
  pluginName: string;
  pluginType: string;
  pluginVersionName: string;
  pluginVersionId: string;
  eventName: string;
  merchantId: string;
}

/** Body sent to the execute-transformation endpoint. The `body` field is
 * JSON-stringified by the caller. `pluginContext`/`channel`/`direction` are
 * optional metadata for execution-log persistence — omitting them preserves
 * today's context-less behaviour. */
export interface RunTransformationBody {
  body: string;
  snippetId: string;
  pluginContext?: TransformPluginContext;
  channel?: 'API' | 'Webhook';
  direction?: 'Inbound' | 'Outbound';
}

/** Response from the execute-transformation endpoint, after `transformResponse`
 * has decoded the wire string. `hasError` signals a snippet-level failure
 * (distinct from an HTTP error); the caller must inspect it and throw when true. */
export interface RunTransformationResult {
  hasError: boolean;
  /** Technical result, stored as the execution log's error message. Shown to the Merchant only when `errorDesc` is not set. */
  resultDesc?: string;
  /** Error text the Merchant sees when a connect or disconnect fails. */
  errorDesc?: string;
  pluginVariables?: Record<string, string>;
  /** `plugin-installed`: schedules the portal must create. See `schedules.ts`. */
  schedulesToCreate?: unknown;
  /** `plugin-uninstalled`: names of `*ScheduleId` variables whose schedules the portal must delete. */
  schedulesToDelete?: unknown;
  [key: string]: unknown;
}

/**
 * Decodes the execute-transformation response. The service JSON-encodes the snippet's
 * object into a string, so the parsed HTTP body is a string, and reading `hasError` off
 * it silently yields undefined — a failed snippet then looks like a success. Anything
 * that does not decode to a plain object is reported as a failure rather than passed on,
 * so an unreadable response can never be mistaken for one.
 */
export function parseRunTransformationResponse(response: unknown): RunTransformationResult {
  const decoded = typeof response === 'string' ? tryParseJson(response) : response;

  if (decoded === null || typeof decoded !== 'object' || Array.isArray(decoded)) {
    return { hasError: true, resultDesc: 'Unreadable transformation response' };
  }

  return decoded as RunTransformationResult;
}

export function getMerchantErrorMessage(result: RunTransformationResult, fallback: string): string {
  // The result is unchecked Snippet JSON, so a field typed as text may hold anything.
  const text = [result.errorDesc, result.resultDesc].find(
    (value): value is string => typeof value === 'string' && value.trim() !== '',
  );
  return text ?? fallback;
}

/**
 * Parses JSON, returning null instead of throwing on malformed input.
 */
function tryParseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

export const transformationApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /transformation/v1/transformations/{id} — full transformation incl. snippets. */
    getTransformationDetails: builder.query<TransformationDetail, string>({
      query: (transformationId) => transformationRequestBuilders.getTransformationDetails(transformationId),
      providesTags: (_r, _e, id) => [{ type: 'Transformation', id }],
    }),
    /** POST /transformation/v1/transformation — create a new transformation (version 0.1). */
    createTransformation: builder.mutation<TransformationWriteResult, TransformationWriteBody>({
      query: (body) => transformationRequestBuilders.createTransformation(body),
    }),
    /** PATCH /transformation/v1/transformation/{id}/createSnippet — add a snippet version. */
    createSnippet: builder.mutation<
      TransformationWriteResult,
      { transformationId: string; data: TransformationWriteBody }
    >({
      query: (args) => transformationRequestBuilders.createSnippet(args),
      invalidatesTags: (_r, _e, { transformationId }) => [{ type: 'Transformation', id: transformationId }],
    }),
    /** PATCH /transformation/v1/transformation/{id}/{snippetId}/promote — promote a snippet to live. */
    promoteTransformation: builder.mutation<void, { transformationId: string; snippetId: string }>({
      query: (args) => transformationRequestBuilders.promoteTransformation(args),
      invalidatesTags: (_r, _e, { transformationId }) => [{ type: 'Transformation', id: transformationId }],
    }),
    /** POST /transformation/v1/transformation/test — run a snippet against a sample payload. */
    testTransformation: builder.mutation<TestTransformationResult, { snippetCode: string; payload: string }>({
      query: (args) => transformationRequestBuilders.testTransformation(args),
    }),
    /** POST /transformation/v1/transformation/{id}/transform — execute a deployed transformation
     * snippet. Used by the StoreFront merchant-connect orchestration to run the plugin-installed
     * and plugin-uninstalled lifecycle snippets. The `body.body` field is JSON-stringified
     * (service wire contract). Returns `{ hasError, pluginVariables, ... }` — callers MUST
     * check `hasError` and throw on failure; an HTTP 200 with hasError=true is a snippet error. */
    runTransformation: builder.mutation<RunTransformationResult, { transformationId: string; body: RunTransformationBody }>({
      query: (args) => transformationRequestBuilders.runTransformation(args),
      transformResponse: parseRunTransformationResponse,
    }),
  }),
});

export const {
  useGetTransformationDetailsQuery,
  useLazyGetTransformationDetailsQuery,
  useCreateTransformationMutation,
  useCreateSnippetMutation,
  usePromoteTransformationMutation,
  useTestTransformationMutation,
  useRunTransformationMutation,
} = transformationApi;
